import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';

interface RequirementSource {
  _id: string;
  professionID: string;
  stateID: number;
  stateName: string;
  boardName: string;
  primarySourceURL: string;
  rulesCitation?: string;
  lastVerifiedBy: string;
  totalHoursRequired: number;
  renewalCycleName: string;
  lastVerifiedAt: string;
  nextReviewDue: string;
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  flaggedForReview: boolean;
  flagNote?: string;
  noCERequired: boolean;
  isOverdue: boolean;
  daysUntilReview: number;
  changeLog: { date: string; summary: string; changedBy: string }[];
}

interface Summary { total: number; overdue: number; flagged: number; lowConfidence: number }

const CONFIDENCE_COLORS: Record<string, string> = {
  HIGH:   'var(--color-success, #16a34a)',
  MEDIUM: 'var(--color-warning, #d97706)',
  LOW:    'var(--color-danger,  #dc2626)',
};

const PROFESSIONS = ['ALL', 'ARCH', 'PE', 'LA', 'CPA', 'PHARM', 'DDS', 'DH', 'LMHC'];

export default function AdminCEAudit() {
  const [sources, setSources] = useState<RequirementSource[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Filters
  const [filterProf, setFilterProf] = useState('ALL');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [filterFlagged, setFilterFlagged] = useState(false);

  // Detail panel
  const [selected, setSelected] = useState<RequirementSource | null>(null);
  const [verifyNote, setVerifyNote] = useState('');

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterProf !== 'ALL') params.professionID = filterProf;
      if (filterOverdue) params.overdueOnly = 'true';
      if (filterFlagged) params.flaggedOnly = 'true';

      const [listRes, summaryRes] = await Promise.all([
        api.get('/api/ce-audit', { params }),
        api.get('/api/ce-audit/summary'),
      ]);
      setSources(listRes.data);
      setSummary(summaryRes.data);
    } catch (e: any) {
      setError('Failed to load CE audit data.');
    } finally {
      setLoading(false);
    }
  }, [filterProf, filterOverdue, filterFlagged]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markVerified = async (id: string) => {
    try {
      await api.post(`/api/ce-audit/${id}/mark-verified`, { note: verifyNote || undefined });
      showToast('success', 'Marked as verified. Review due in 60 days.');
      setVerifyNote('');
      setSelected(null);
      fetchData();
    } catch { showToast('error', 'Failed to mark as verified.'); }
  };

  const runStalenessCheck = async () => {
    try {
      const res = await api.post('/api/ce-audit/run-staleness-check');
      showToast('success', `Staleness check complete — ${res.data.flaggedCount} record(s) newly flagged.`);
      fetchData();
    } catch { showToast('error', 'Staleness check failed.'); }
  };

  const statusBadge = (s: RequirementSource) => {
    if (s.isOverdue)        return { label: 'OVERDUE',  bg: '#fee2e2', color: '#b91c1c' };
    if (s.flaggedForReview) return { label: 'FLAGGED',  bg: '#fef3c7', color: '#92400e' };
    if (s.daysUntilReview <= 14) return { label: 'DUE SOON', bg: '#e0f2fe', color: '#0369a1' };
    return { label: 'CURRENT', bg: '#dcfce7', color: '#15803d' };
  };

  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div style={{ padding: '2rem', fontFamily: 'IBM Plex Sans, system-ui, sans-serif', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0F1F3D', margin: 0 }}>
          CE Requirements Audit
        </h1>
        <p style={{ color: '#6b7280', marginTop: 4 }}>
          Tracks official source URLs, verification dates, and staleness flags for every profession × state combination.
          Bi-monthly cron auto-flags overdue records.
        </p>
      </div>

      {/* Summary cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Records',   value: summary.total,         bg: '#f1f5f9', color: '#0F1F3D' },
            { label: 'Overdue Review',  value: summary.overdue,       bg: '#fee2e2', color: '#b91c1c' },
            { label: 'Flagged',         value: summary.flagged,        bg: '#fef3c7', color: '#92400e' },
            { label: 'Low Confidence',  value: summary.lowConfidence,  bg: '#ede9fe', color: '#5b21b6' },
          ].map(card => (
            <div key={card.label} style={{
              background: card.bg, borderRadius: 10, padding: '1rem 1.25rem',
              border: `1px solid ${card.bg === '#f1f5f9' ? '#e2e8f0' : card.bg}`,
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: card.color }}>{card.value}</div>
              <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: 2 }}>{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          value={filterProf}
          onChange={e => setFilterProf(e.target.value)}
          style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.875rem' }}
        >
          {PROFESSIONS.map(p => <option key={p}>{p}</option>)}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={filterOverdue} onChange={e => setFilterOverdue(e.target.checked)} />
          Overdue only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={filterFlagged} onChange={e => setFilterFlagged(e.target.checked)} />
          Flagged only
        </label>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={runStalenessCheck}
            style={{
              padding: '0.4rem 1rem', borderRadius: 6, border: '1px solid #d1d5db',
              background: '#fff', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500,
            }}
          >
            Run Staleness Check
          </button>
          <button
            onClick={fetchData}
            style={{
              padding: '0.4rem 1rem', borderRadius: 6, border: 'none',
              background: '#0F1F3D', color: '#fff', fontSize: '0.875rem', cursor: 'pointer', fontWeight: 500,
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading…</div>
      ) : error ? (
        <div style={{ color: '#b91c1c', padding: '1rem' }}>{error}</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Prof', 'State', 'Hours / Cycle', 'Board', 'Last Verified', 'Next Review', 'Confidence', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sources.map((s, i) => {
                const badge = statusBadge(s);
                return (
                  <tr
                    key={s._id}
                    style={{
                      background: i % 2 === 0 ? '#fff' : '#f9fafb',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                    }}
                    onClick={() => { setSelected(s); setVerifyNote(''); }}
                  >
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, color: '#0F1F3D' }}>{s.professionID}</td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>{s.stateName}</td>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>
                      {s.noCERequired ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No CE req.</span>
                        : <>{s.totalHoursRequired} hrs / {s.renewalCycleName}</>}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a href={s.primarySourceURL} target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ color: '#2563eb', textDecoration: 'none' }}
                        title={s.boardName}
                      >
                        {s.boardName}
                      </a>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap', color: '#374151' }}>{fmt(s.lastVerifiedAt)}</td>
                    <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap', color: s.isOverdue ? '#b91c1c' : '#374151' }}>
                      {fmt(s.nextReviewDue)}
                      {s.isOverdue && <span style={{ marginLeft: 4, fontSize: '0.75rem', fontWeight: 600, color: '#b91c1c' }}>
                        ({Math.abs(s.daysUntilReview)}d ago)
                      </span>}
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em',
                        background: `${CONFIDENCE_COLORS[s.confidenceLevel]}22`,
                        color: CONFIDENCE_COLORS[s.confidenceLevel],
                      }}>
                        {s.confidenceLevel}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                        fontSize: '0.75rem', fontWeight: 600, background: badge.bg, color: badge.color,
                      }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.75rem' }}>
                      <span style={{ color: '#9ca3af', fontSize: '1rem' }}>›</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sources.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No records match the current filter.</div>
          )}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', justifyContent: 'flex-end', zIndex: 50,
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              width: 480, height: '100%', background: '#fff', overflow: 'auto',
              padding: '1.5rem', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#0F1F3D' }}>
                {selected.professionID} — {selected.stateName}
              </h2>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            <Section label="Licensing Board">
              <a href={selected.primarySourceURL} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
                {selected.boardName}
              </a>
            </Section>

            <Section label="CE Requirement">
              {selected.noCERequired
                ? <span style={{ color: '#6b7280', fontStyle: 'italic' }}>No CE required by this state.</span>
                : <>{selected.totalHoursRequired} hours / {selected.renewalCycleName}</>}
            </Section>

            {selected.rulesCitation && <Section label="Rule Citation">{selected.rulesCitation}</Section>}

            <Section label="Last Verified">
              {fmt(selected.lastVerifiedAt)} by {selected.lastVerifiedBy}
            </Section>
            <Section label="Next Review Due">
              <span style={{ color: selected.isOverdue ? '#b91c1c' : '#15803d', fontWeight: 600 }}>
                {fmt(selected.nextReviewDue)}
                {selected.isOverdue ? ` (${Math.abs(selected.daysUntilReview)} days overdue)` : ` (${selected.daysUntilReview} days)`}
              </span>
            </Section>

            {selected.flaggedForReview && selected.flagNote && (
              <div style={{
                background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8,
                padding: '0.75rem', marginBottom: '1rem',
              }}>
                <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 4 }}>⚠ Flag Note</div>
                <div style={{ fontSize: '0.85rem', color: '#78350f' }}>{selected.flagNote}</div>
              </div>
            )}

            {/* Mark verified */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: '0.875rem' }}>
                Mark as Verified
              </div>
              <textarea
                value={verifyNote}
                onChange={e => setVerifyNote(e.target.value)}
                placeholder="Optional note (e.g. 'Checked TBAE.gov — no changes')"
                rows={2}
                style={{
                  width: '100%', resize: 'vertical', padding: '0.5rem',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem',
                  fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={() => markVerified(selected._id)}
                style={{
                  marginTop: 8, width: '100%', padding: '0.5rem', borderRadius: 6,
                  border: 'none', background: '#0F1F3D', color: '#fff',
                  fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                ✓ Mark Verified (reset 60-day clock)
              </button>
            </div>

            {/* Change log */}
            <div style={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem', marginBottom: 8 }}>
              Change History
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...selected.changeLog].reverse().map((entry, i) => (
                <div key={i} style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 10 }}>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {fmt(entry.date)} · {entry.changedBy}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#374151' }}>{entry.summary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 100,
          padding: '0.75rem 1.25rem', borderRadius: 8, fontWeight: 500, fontSize: '0.875rem',
          background: toast.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: toast.type === 'success' ? '#15803d' : '#b91c1c',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>{children}</div>
    </div>
  );
}
